import React, { useEffect, useState } from 'react';
import { backofficeDb, backofficeAuth } from '../backofficeFirebaseClient';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const defaultSettings = {
  apiBaseUrls: {
    public: 'https://acctoo-public.example.com/api',
    backoffice: 'https://us-central1-acctoo-backoffice.cloudfunctions.net/api'
  },
  payment: {
    provider: 'razorpay',
    razorpay: { keyId: '', keySecret: '' },
    cashfree: { appId: '', secretKey: '' }
  },
  sms: {
    provider: 'msg91',
    msg91: { authKey: '', senderId: '' },
    twilio: { accountSid: '', authToken: '', from: '' }
  }
};

const ApiSettings = ({ role }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const ref = doc(backofficeDb, 'admin_settings', 'api_settings');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setSettings({ ...defaultSettings, ...data });
        } else {
          setSettings(defaultSettings);
        }
        setMessage('');
      } catch (e) {
        setMessage(e.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    if (role !== 'super_admin') { setMessage('Only super admins can save.'); return; }

    const hasSecrets =
      !!(settings.payment?.razorpay?.keySecret ||
        settings.payment?.cashfree?.secretKey ||
        settings.sms?.msg91?.authKey ||
        settings.sms?.twilio?.authToken);
    if (hasSecrets) {
      const ok = window.confirm(
        'You are about to persist payment/SMS secrets into Firestore. Confirm this environment is trusted, admin rules restrict reads, and you will rotate keys if this screen was ever exposed. Continue?'
      );
      if (!ok) return;
    }

    try {
      setSaving(true);
      const ref = doc(backofficeDb, 'admin_settings', 'api_settings');
      const user = backofficeAuth.currentUser;
      await setDoc(ref, {
        ...settings,
        updatedAt: serverTimestamp(),
        updatedBy: { uid: user?.uid || 'unknown', email: user?.email || 'unknown' }
      }, { merge: true });
      setMessage('Saved');
    } catch (e) {
      setMessage(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (role !== 'super_admin') {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="text-lg font-semibold">Access Restricted</div>
          <div className="text-sm text-gray-600 mt-1">Only super admins can view API Settings.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold">API Settings</h3>
          {message && <div className="text-sm text-gray-600">{message}</div>}
        </div>

        <div className="bg-amber-50 border border-amber-200 text-amber-950 text-sm rounded-lg p-4 space-y-2">
          <p className="font-semibold">Security</p>
          <p>
            Live payment and SMS credentials should ultimately live in server-side secrets (Cloud Functions / Secret Manager).
            Values saved here are stored in Firestore — restrict reads with rules, never expose this page on shared screens, and rotate keys after incidents.
          </p>
        </div>

        {/* Base URLs */}
        <div>
          <div className="font-medium mb-2">Base URLs</div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col">
              <span className="text-sm text-gray-600 mb-1">Public API Base URL</span>
              <input
                className="border rounded px-3 py-2"
                value={settings.apiBaseUrls.public}
                onChange={(e) => setSettings({ ...settings, apiBaseUrls: { ...settings.apiBaseUrls, public: e.target.value } })}
              />
            </label>
            <label className="flex flex-col">
              <span className="text-sm text-gray-600 mb-1">Backoffice API Base URL</span>
              <input
                className="border rounded px-3 py-2"
                value={settings.apiBaseUrls.backoffice}
                onChange={(e) => setSettings({ ...settings, apiBaseUrls: { ...settings.apiBaseUrls, backoffice: e.target.value } })}
              />
            </label>
          </div>
        </div>

        {/* Payment Providers */}
        <div>
          <div className="font-medium mb-2">Payment Provider</div>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="flex flex-col">
              <span className="text-sm text-gray-600 mb-1">Provider</span>
              <select
                className="border rounded px-3 py-2"
                value={settings.payment.provider}
                onChange={(e) => setSettings({ ...settings, payment: { ...settings.payment, provider: e.target.value } })}
              >
                <option value="razorpay">Razorpay</option>
                <option value="cashfree">Cashfree</option>
              </select>
            </label>
            {settings.payment.provider === 'razorpay' && (
              <>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">Razorpay Key ID</span>
                  <input
                    className="border rounded px-3 py-2"
                    value={settings.payment.razorpay.keyId}
                    onChange={(e) => setSettings({ ...settings, payment: { ...settings.payment, razorpay: { ...settings.payment.razorpay, keyId: e.target.value } } })}
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">Razorpay Key Secret</span>
                  <input
                    className="border rounded px-3 py-2"
                    type="password"
                    value={settings.payment.razorpay.keySecret}
                    onChange={(e) => setSettings({ ...settings, payment: { ...settings.payment, razorpay: { ...settings.payment.razorpay, keySecret: e.target.value } } })}
                  />
                </label>
              </>
            )}
            {settings.payment.provider === 'cashfree' && (
              <>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">Cashfree App ID</span>
                  <input
                    className="border rounded px-3 py-2"
                    value={settings.payment.cashfree.appId}
                    onChange={(e) => setSettings({ ...settings, payment: { ...settings.payment, cashfree: { ...settings.payment.cashfree, appId: e.target.value } } })}
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">Cashfree Secret Key</span>
                  <input
                    className="border rounded px-3 py-2"
                    type="password"
                    value={settings.payment.cashfree.secretKey}
                    onChange={(e) => setSettings({ ...settings, payment: { ...settings.payment, cashfree: { ...settings.payment.cashfree, secretKey: e.target.value } } })}
                  />
                </label>
              </>
            )}
          </div>
        </div>

        {/* SMS Providers */}
        <div>
          <div className="font-medium mb-2">SMS Provider</div>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="flex flex-col">
              <span className="text-sm text-gray-600 mb-1">Provider</span>
              <select
                className="border rounded px-3 py-2"
                value={settings.sms.provider}
                onChange={(e) => setSettings({ ...settings, sms: { ...settings.sms, provider: e.target.value } })}
              >
                <option value="msg91">MSG91</option>
                <option value="twilio">Twilio</option>
              </select>
            </label>
            {settings.sms.provider === 'msg91' && (
              <>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">MSG91 Auth Key</span>
                  <input
                    className="border rounded px-3 py-2"
                    value={settings.sms.msg91.authKey}
                    onChange={(e) => setSettings({ ...settings, sms: { ...settings.sms, msg91: { ...settings.sms.msg91, authKey: e.target.value } } })}
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">Sender ID</span>
                  <input
                    className="border rounded px-3 py-2"
                    value={settings.sms.msg91.senderId}
                    onChange={(e) => setSettings({ ...settings, sms: { ...settings.sms, msg91: { ...settings.sms.msg91, senderId: e.target.value } } })}
                  />
                </label>
              </>
            )}
            {settings.sms.provider === 'twilio' && (
              <>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">Account SID</span>
                  <input
                    className="border rounded px-3 py-2"
                    value={settings.sms.twilio.accountSid}
                    onChange={(e) => setSettings({ ...settings, sms: { ...settings.sms, twilio: { ...settings.sms.twilio, accountSid: e.target.value } } })}
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">Auth Token</span>
                  <input
                    className="border rounded px-3 py-2"
                    type="password"
                    value={settings.sms.twilio.authToken}
                    onChange={(e) => setSettings({ ...settings, sms: { ...settings.sms, twilio: { ...settings.sms.twilio, authToken: e.target.value } } })}
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">From Number</span>
                  <input
                    className="border rounded px-3 py-2"
                    value={settings.sms.twilio.from}
                    onChange={(e) => setSettings({ ...settings, sms: { ...settings.sms, twilio: { ...settings.sms.twilio, from: e.target.value } } })}
                  />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Example calls */}
        <div>
          <div className="font-medium mb-2">API Calling Examples</div>
          <div className="text-xs text-gray-600 mb-2">Use Firebase ID token in Authorization header</div>
          <pre className="bg-gray-50 border rounded p-3 text-xs overflow-auto">
{`// Example: assign subscription (admin)
const token = await backofficeAuth.currentUser.getIdToken();
await fetch('${settings.apiBaseUrls.backoffice}/v1/admin/companies/C101/assign-subscription', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
  body: JSON.stringify({ planKey: 'pro', paymentMode: 'offline' })
});

// Example: upsert user (app)
const idToken = await firebaseAuth.currentUser.getIdToken();
await fetch('${settings.apiBaseUrls.backoffice}/v1/users', {
  method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + idToken },
  body: JSON.stringify({ uid: firebaseAuth.currentUser.uid, name: 'Amit Kumar' })
});`}
          </pre>
        </div>

        <div className="flex justify-end">
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={save}
            disabled={saving || loading}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiSettings;


